import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon';
import { bool, func, number, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Agent from 'services/api/Agent';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  treeBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    maxHeight: 360,
    overflowY: 'auto',
    padding: theme.spacing(1, 0.5),
  },
  treeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minHeight: 30,
    paddingRight: theme.spacing(1),
  },
  chevronBtn: {
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    cursor: 'pointer',
  },
  chevronSpacer: { width: 24, flexShrink: 0 },
  label: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.primary,
    flex: 1,
  },
  count: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
});

function collectKeys(node, acc = []) {
  acc.push(node.key);
  (node.children || []).forEach(child => collectKeys(child, acc));
  return acc;
}

const TreeRow = ({
  node,
  depth = 0,
  selected,
  expanded,
  onToggleSelect,
  onToggleExpand,
  styles,
}) => {
  const hasChildren = (node.children || []).length > 0;
  const isExpanded = expanded.has(node.key);
  return (
    <>
      <Box sx={[styles.treeRow, { pl: depth * 2 }]}>
        {hasChildren ? (
          <Box
            component="span"
            sx={styles.chevronBtn}
            role="button"
            tabIndex={0}
            onClick={() => onToggleExpand(node.key)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggleExpand(node.key);
              }
            }}
          >
            {isExpanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          </Box>
        ) : (
          <Box component="span" sx={styles.chevronSpacer} />
        )}
        <Checkbox
          size="small"
          checked={selected.has(node.key)}
          disabled={node.required}
          onChange={() => onToggleSelect(node.key, node)}
          sx={{ p: 0.25 }}
        />
        <Typography sx={styles.label}>{node.label}</Typography>
      </Box>
      {hasChildren && (
        <Collapse in={isExpanded}>
          {(node.children || []).map(child => (
            <TreeRow
              key={child.key}
              node={child}
              depth={depth + 1}
              selected={selected}
              expanded={expanded}
              onToggleSelect={onToggleSelect}
              onToggleExpand={onToggleExpand}
              styles={styles}
            />
          ))}
        </Collapse>
      )}
    </>
  );
};

TreeRow.propTypes = {
  node: object.isRequired,
  depth: number,
  selected: object.isRequired,
  expanded: object.isRequired,
  onToggleSelect: func.isRequired,
  onToggleExpand: func.isRequired,
  styles: object.isRequired,
};

const ExportAgentModal = ({ open, agentId = null, agentName = '', onClose }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [root, setRoot] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    if (!open || !agentId) return;
    setLoading(true);
    Agent.exportGraph(agentId)
      .then(data => {
        setRoot(data.root);
        const allKeys = collectKeys(data.root, []);
        setSelected(new Set(allKeys));
        setExpanded(new Set([data.root?.key].filter(Boolean)));
      })
      .finally(() => setLoading(false));
  }, [open, agentId]);

  const selectedCount = selected.size;

  const onToggleExpand = key => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onToggleSelect = (key, node) => {
    setSelected(prev => {
      const next = new Set(prev);
      const subtreeKeys = collectKeys(node, []);
      const isSelected = next.has(key);
      if (isSelected) {
        subtreeKeys.forEach(k => next.delete(k));
      } else {
        subtreeKeys.forEach(k => next.add(k));
      }
      return next;
    });
  };

  const handleExport = async () => {
    if (!agentId || !selected.size) return;
    setExporting(true);
    try {
      const blob = await Agent.exportBundle(agentId, [...selected]);
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `${agentName || 'agent'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      onClose();
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {I18n.t('AgenticChatFlow.ExportAgent')}
        <IconButton size="small" onClick={onClose} aria-label={I18n.t('Common.close')}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          {I18n.t('AgenticChatFlow.ExportAgentHelper')}
        </Typography>
        <Box sx={styles.treeBox}>
          {loading && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                p: 2,
              }}
            >
              {I18n.t('Common.Loading')}
            </Typography>
          )}
          {!loading && root && (
            <TreeRow
              node={root}
              depth={0}
              selected={selected}
              expanded={expanded}
              onToggleSelect={onToggleSelect}
              onToggleExpand={onToggleExpand}
              styles={styles}
            />
          )}
        </Box>
        <Typography sx={styles.count}>
          {I18n.t('AgenticChatFlow.ExportEntitiesSelected', { count: selectedCount })}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
          {I18n.t('AgenticChatFlow.Cancel')}
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          disableElevation
          disabled={exporting || !selectedCount}
          startIcon={<TrayArrowDownIcon size={16} />}
          sx={{ textTransform: 'none' }}
        >
          {I18n.t('AgenticChatFlow.Export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExportAgentModal.propTypes = {
  open: bool.isRequired,
  agentId: string,
  agentName: string,
  onClose: func.isRequired,
};

export default ExportAgentModal;
